import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Star, Tag, Play, ArrowRight } from 'lucide-react';
import TrustLogosSection from '../trust/TrustLogosSection';
import HeroBadgesSection from './HeroBadgesSection';
import { DemoBookingModal } from '../DemoBookingModal';
import { SupademoModal } from '../SupademoModal';
import { LazyImage } from '../ui/lazy-image';
import heroRecognitionPlatform from '@/assets/hero-recognition-platform.webp';
import heroRecognitionPlatformMobile from '@/assets/hero-recognition-platform-mobile.webp';
import { usePostHog } from '@/hooks/usePostHog';


export function OptimizedHero() {
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const [isSupademoModalOpen, setIsSupademoModalOpen] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);
  const [demoEmail, setDemoEmail] = useState<string | null>(null);
  const { posthog } = usePostHog();

  // Preload critical hero image and handle hydration
  useEffect(() => {
    const heroImage = '/lovable-uploads/552f9bfa-16cf-4640-a041-1d0d4fe63829.png';
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = heroImage;
    document.head.appendChild(link);

    // Enable animations after hydration
    setIsHydrated(true);

    // Check for query parameter to auto-open demo modal
    const urlParams = new URLSearchParams(window.location.search);
    const emailParam = urlParams.get('demo_email');
    if (urlParams.get('demo') === 'true' || emailParam) {
      setDemoEmail(emailParam);
      setIsSupademoModalOpen(true);
    } else if (urlParams.get('book_demo') === 'true') {
      setIsDemoModalOpen(true);
    }
  }, []);

  return (
    <div className="relative bg-background font-sans overflow-hidden min-h-screen">
      {/* Modern Subtle Gradient Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Refined gradient orbs in blue spectrum - static, no animation */}
        <div className="absolute top-1/3 -left-40 w-[500px] h-[500px] bg-gradient-to-br from-indigo-400/10 via-blue-500/8 to-slate-400/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-[450px] h-[450px] bg-gradient-to-br from-blue-400/10 via-indigo-400/8 to-slate-300/6 rounded-full blur-3xl"></div>
      </div>
      
      {/* Dot Grid Pattern with better visibility */}
      <div className="absolute inset-0 opacity-[0.15] pointer-events-none">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, rgba(99, 102, 241, 0.4) 2px, transparent 0)`,
          backgroundSize: '48px 48px'
        }}></div>
      </div>
      
      {/* Noise Texture Overlay */}
      <div className="absolute inset-0 opacity-[0.03] mix-blend-overlay pointer-events-none">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='3' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          backgroundRepeat: 'repeat'
        }}></div>
      </div>
      

      <div className="pt-[120px] sm:pt-[110px] lg:pt-20">
      </div>

      <section className="relative flex items-center justify-center py-8 lg:py-10 px-4 lg:px-8 min-h-[calc(100vh-120px)]">
        <div className="container mx-auto px-4 2xl:px-12 max-w-none lg:max-w-7xl xl:max-w-[1400px] 2xl:max-w-[1600px] relative px-6 lg:px-6 xl:px-8 pb-3 lg:pb-4">
          <div className="flex flex-col lg:grid lg:grid-cols-7 gap-6 lg:gap-4 items-start">
            {/* Left Section: Text Content with CSS animations */}
            <div className="flex flex-col items-start text-left lg:col-span-4 h-full justify-center space-y-4 lg:space-y-6">
              {/* Pills with CSS animation */}
              <div className={`flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-2 sm:gap-3 mt-6 lg:mt-8 mb-3 lg:mb-4 ${isHydrated ? 'animate-fade-in' : 'opacity-0'}`} style={isHydrated ? {
                animationDelay: '0ms',
                animationFillMode: 'forwards'
              } : {}}>
                <div className="relative overflow-hidden px-3 py-1.5 rounded-full bg-black/5 border border-black/10 backdrop-blur-sm hover:bg-black/10 transition-all duration-300 hover:-translate-y-0.5">
                  <h1 className="text-xs font-normal text-gray-900 tracking-wide uppercase">Best Employee Recognition & Rewards Platform</h1>
                </div>
              </div>

              {/* Headline with CSS animation */}
              <div className="mb-4 lg:mb-6">
                <h2 className={`text-5xl sm:text-6xl md:text-7xl lg:text-6xl xl:text-7xl font-bold text-gray-900 tracking-tight font-sans ${isHydrated ? 'animate-fade-in' : 'opacity-0'}`} style={isHydrated ? {
                  animationDelay: '200ms',
                  animationFillMode: 'forwards'
                } : {}}>
                  <span className="block mb-0 sm:mb-0 md:mb-0 lg:mb-4">Recognition that drives</span>
                  <span className="block">
                    <span> </span>
                    <span className="relative inline-block font-extrabold">
                      Culture
                      <span className="absolute inset-x-0 bottom-1 h-3 bg-gradient-to-r from-blue-200/60 via-blue-300/70 to-blue-200/60 rounded-sm transform scale-x-0 origin-left transition-transform duration-1000 ease-out -z-10" style={isHydrated ? { transform: 'scaleX(1)', transitionDelay: '200ms' } : {}}></span>
                    </span>
                    <span className="mx-2"> and </span>
                    <span className="relative inline-block font-extrabold">
                      Retention
                      <span className="absolute inset-x-0 bottom-1 h-3 bg-gradient-to-r from-emerald-200/60 via-emerald-300/70 to-emerald-200/60 rounded-sm transform scale-x-0 origin-left transition-transform duration-1000 ease-out -z-10" style={isHydrated ? { transform: 'scaleX(1)', transitionDelay: '200ms' } : {}}></span>
                    </span>
                  </span>
                </h2>
              </div>

              {/* Subheading with CSS animation */}
              <p className={`text-sm sm:text-base lg:text-base xl:text-lg text-gray-600 mb-6 lg:mb-8 leading-relaxed ${isHydrated ? 'animate-fade-in' : 'opacity-0'}`} style={isHydrated ? {
                animationDelay: '400ms',
                animationFillMode: 'forwards'
              } : {}}>Employee recognition that feels authentic, rewards your team values, and celebrations that make milestones count — all in one award-winning platform, built for real teams and real impact.</p>
              
              {/* Action Buttons with CSS animation */}
              <div className={`mb-4 lg:mb-6 w-full ${isHydrated ? 'animate-fade-in' : 'opacity-0'}`} style={isHydrated ? {
                animationDelay: '600ms',
                animationFillMode: 'forwards'
              } : {}}>
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full sm:w-auto">
                  <Button 
                    size="lg" 
                    className="group relative w-full sm:w-auto bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-bold py-3 px-6 lg:py-4 lg:px-8 xl:py-5 xl:px-10 2xl:py-6 2xl:px-12 rounded-xl text-sm sm:text-base lg:text-base xl:text-lg shadow-lg shadow-blue-600/25 hover:shadow-xl hover:shadow-blue-600/40 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] border-0 overflow-hidden" 
            onClick={() => {
              posthog?.capture('book_demo_clicked', {
                page: window.location.pathname,
                section: 'hero',
                url: window.location.href
              });
              setIsDemoModalOpen(true);
            }}
                  >
                    <span className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                    <span className="relative z-10 flex items-center justify-center gap-2">
                      Book a Demo
                      <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </span>
                  </Button>
                <Button 
                  variant="outline" 
                  size="lg" 
                  className="group relative w-full sm:w-auto border-2 border-blue-200 bg-white/80 backdrop-blur-sm text-blue-700 hover:text-blue-800 hover:bg-blue-50/80 hover:border-blue-300 font-semibold py-3 px-6 lg:py-4 lg:px-8 xl:py-5 xl:px-10 2xl:py-6 2xl:px-12 rounded-xl text-sm sm:text-base lg:text-base xl:text-lg shadow-md shadow-blue-100/50 hover:shadow-lg hover:shadow-blue-200/60 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] overflow-hidden" 
            onClick={() => {
              posthog?.capture('see_it_in_action_clicked', {
                page: window.location.pathname,
                section: 'hero',
                button_text: 'See it in Action',
                url: window.location.href,
                opens_modal: true
              });
              setIsSupademoModalOpen(true);
            }}
                >
                    <span className="absolute inset-0 bg-gradient-to-r from-blue-50/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                    <span className="relative z-10 flex items-center justify-center gap-2">
                      See it in Action
                      <Play className="w-4 h-4 transition-transform duration-300 group-hover:scale-110" fill="currentColor" />
                    </span>
                  </Button>
                </div>
              </div>
              
              {/* Trust Ratings with CSS animation */}
              <div className={`flex flex-col sm:flex-row gap-3 sm:gap-6 items-start sm:items-center w-full ${isHydrated ? 'animate-fade-in' : 'opacity-0'}`} style={isHydrated ? {
                animationDelay: '800ms',
                animationFillMode: 'forwards'
              } : {}}>
                <div className="flex items-center gap-2">
                  <Star className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-400 fill-yellow-400" />
                  <span className="text-sm font-semibold text-gray-700">4.9 / 5 on G2</span>
                  <LazyImage src="/lovable-uploads/e68d106f-69a9-45e8-8624-7f09b1ae1e6f.png" alt="G2 Logo" className="h-4 w-4 sm:h-5 sm:w-5" width={20} height={20} />
                </div>
                <a href="https://www.g2.com/products/assembly-2021-12-09/reviews" target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-opacity">
                  <span className="text-sm font-semibold text-gray-500">3000+ reviews</span>
                </a>
              </div>
            </div>
            
            {/* Hero Image Section - appears after text on mobile/tablet, side by side on desktop */}
            <div className="relative w-full flex items-center justify-center lg:col-span-3 mt-6 lg:mt-0">
              <div className="relative group">
                {/* Image container */}
                <div className="relative">
                  <picture>
                    {/* Mobile-optimized image for screens up to 768px */}
                    <source 
                      media="(max-width: 768px)" 
                      srcSet={heroRecognitionPlatformMobile}
                      width={640}
                      height={853}
                    />
                    {/* Desktop image for larger screens */}
                    <img 
                      src={heroRecognitionPlatform} 
                      alt="Assembly employee recognition platform interface showing team recognition notifications, peer-to-peer appreciation, birthday celebrations, points system, and reward redemptions including Starbucks gift cards and charity donations" 
                      className={`w-full h-auto object-cover rounded-xl transition-transform duration-300 group-hover:scale-105 ${isHydrated ? 'animate-scale-in' : 'opacity-0'}`} 
                      style={isHydrated ? {
                        animationDelay: '400ms',
                        animationFillMode: 'forwards'
                      } : {}} 
                      loading="eager" 
                      decoding="async" 
                      fetchPriority="high" 
                      width={900} 
                      height={800} 
                    />
                  </picture>
                </div>
              </div>
            </div>
          </div>
          
          {/* Trust Section at Bottom */}
          <div className={`text-center pt-8 lg:pt-12 mt-8 space-y-8 ${isHydrated ? 'animate-fade-in' : 'opacity-0'}`} style={isHydrated ? {
            animationDelay: '1000ms',
            animationFillMode: 'forwards'
          } : {}}>
            <p className="text-lg text-gray-600">
              The #1 Employee Recognition & Rewards Platform - Trusted by Leading Teams Worldwide
            </p>
            <HeroBadgesSection />
            <div className="pt-4">
              <TrustLogosSection />
            </div>
          </div>
        </div>
      </section>

      <DemoBookingModal open={isDemoModalOpen} onOpenChange={setIsDemoModalOpen} />
      <SupademoModal open={isSupademoModalOpen} onOpenChange={setIsSupademoModalOpen} email={demoEmail} />
    </div>
  );
}

export default OptimizedHero;